import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../db';
import { calculateWorkoutVolume } from '../utils/exerciseUtils';
import { Users, UserPlus, Inbox, Activity, Copy, Check, X, Clock, Dumbbell, UserX, Mail, Swords } from 'lucide-react';
import { getLevelInfo } from '../utils/xpUtils';
import { toast } from '../utils/feedback';
import UserAvatar from '../components/UserAvatar';
import WorldBossView from '../components/WorldBossView';

const Social = ({ currentProfile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') || 'feed';
  
  const [activeTab, setActiveTab] = useState(initialTab); // 'feed', 'friends', 'requests', 'add'
  const [friendCode, setFriendCode] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [feed, setFeed] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [loadingFeed, setLoadingFeed] = useState(true);
  const [showRemoveModal, setShowRemoveModal] = useState(null);

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [location.search]);

  // Extract relations
  const friendsList = currentProfile?.friends || [];
  const receivedRequests = currentProfile?.receivedRequests || [];
  const sentRequests = currentProfile?.sentRequests || [];

  // Initialize
  useEffect(() => {
    const initSocial = async () => {
      if (!currentProfile) return;
      
      // 1. Check and generate friend code
      const code = await db.checkAndGenerateFriendCode(currentProfile.id);
      setFriendCode(code);
      
      // 2. Fetch all profiles to resolve names/avatars easily
      const allP = await db.getProfiles();
      const pMap = {};
      allP.forEach(p => pMap[p.id] = p);
      setProfiles(pMap);
      
      // 3. Fetch feed
      const friendsAndMe = [...friendsList, currentProfile.id];
      const feedData = await db.getFriendsFeed(friendsAndMe);
      setFeed(feedData);
      setLoadingFeed(false);
    };
    initSocial();
  }, [currentProfile, friendsList.length]); // Refresh if friends list changes

  const handleCopyCode = () => {
    navigator.clipboard.writeText(friendCode);
    toast.success("Code copié !");
  };

  const handleSendRequestByCode = async (e) => {
    e.preventDefault();
    if (!codeInput.trim()) return;
    try {
      const name = await db.sendFriendRequestByCode(currentProfile.id, codeInput.trim().toUpperCase());
      toast.success(`Demande envoyée à ${name} !`);
      setCodeInput('');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAcceptRequest = async (profileId) => {
    await db.acceptFriendRequest(currentProfile.id, profileId);
    toast.success("Demande acceptée !");
  };

  const handleRejectRequest = async (profileId) => {
    await db.rejectFriendRequest(currentProfile.id, profileId);
    toast.success("Demande refusée.");
  };

  const handleRemoveFriend = async (profileId) => {
    await db.removeFriend(currentProfile.id, profileId);
    toast.success("Ami retiré.");
    setShowRemoveModal(null);
  };

  const handleCancelRequest = async (profileId) => {
    await db.cancelFriendRequest(currentProfile.id, profileId);
    toast.success("Demande annulée.");
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    navigate(`/social?tab=${tab}`, { replace: true });
  };

  if (!currentProfile) return <div className="text-center mt-3">Connecte-toi pour voir le réseau social.</div>;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Users size={28} color="var(--primary-color)" />
        <h2>Social</h2>
      </div>

      <div className="social-categories-tabs" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', overflowX: 'auto', paddingBottom: '0.5rem', paddingTop: '8px', paddingRight: '15px' }}>
        <button className={`btn social-tab-feed ${activeTab === 'feed' ? 'btn-primary' : 'btn-outline'}`} onClick={() => handleTabClick('feed')} style={{ flex: '1 0 auto' }}>
          <Activity size={18} /> Activité
        </button>
        <button className={`btn social-tab-boss ${activeTab === 'boss' ? 'btn-primary' : 'btn-outline'}`} onClick={() => handleTabClick('boss')} style={{ flex: '1 0 auto' }}>
          <Swords size={18} /> World Boss
        </button>
        <button className={`btn social-tab-friends ${activeTab === 'friends' ? 'btn-primary' : 'btn-outline'}`} onClick={() => handleTabClick('friends')} style={{ flex: '1 0 auto' }}>
          <Users size={18} /> Amis
        </button>
        <button className={`btn social-tab-add ${activeTab === 'add' ? 'btn-primary' : 'btn-outline'}`} onClick={() => handleTabClick('add')} style={{ flex: '1 0 auto' }}>
          <UserPlus size={18} /> Ajouter
        </button>
        <button className={`btn social-tab-requests ${activeTab === 'requests' ? 'btn-primary' : 'btn-outline'}`} onClick={() => handleTabClick('requests')} style={{ flex: '1 0 auto', position: 'relative' }}>
          <Inbox size={18} /> Demandes
          {receivedRequests.length > 0 && (
            <span style={{ position: 'absolute', top: '-6px', right: '-4px', backgroundColor: 'var(--danger-color)', color: '#fff', borderRadius: '50%', width: '20px', height: '20px', fontSize: '0.72rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', zIndex: 2 }}>
              {receivedRequests.length}
            </span>
          )}
        </button>
      </div>

      {activeTab === 'feed' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {loadingFeed ? (
            <div className="text-center">Chargement du fil d'actualité...</div>
          ) : friendsList.length === 0 ? (
            <div className="card text-center text-secondary" style={{ padding: '2rem 1rem' }}>
              Tu n'as pas encore d'amis pour voir un fil d'actualité.
            </div>
          ) : feed.length === 0 ? (
            <div className="card text-center text-secondary">
              Aucune activité récente parmi tes amis.
            </div>
          ) : (
            feed.map(event => {
              const p = profiles[event.profileId];
              if (!p) return null;
              const date = new Date(event.feedDate).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
              
              if (event.type === 'post') {
                return (
                  <div key={`post-${event.id}`} className="card" style={{ padding: '1rem' }}>
                    <div className="flex items-center gap-3 mb-3">
                      <UserAvatar profile={p} size={40} onClick={() => navigate(`/profile/${p.id}`)} style={{ flexShrink: 0 }} />
                      <div>
                        <strong 
                          style={{ fontSize: '1.05rem', cursor: 'pointer' }} 
                          onClick={() => navigate(`/profile/${p.id}`)}
                          onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                          onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
                        >
                          {p.name}
                        </strong>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>a publié dans le forum • {date}</div>
                      </div>
                    </div>
                    {event.text && (
                      <div style={{ padding: '0.8rem', backgroundColor: 'var(--bg-color)', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--border-color)', fontSize: '0.95rem', whiteSpace: 'pre-wrap' }}>
                        {event.text}
                      </div>
                    )}
                    {event.imageUrl && (
                      <div style={{ marginTop: '0.8rem' }}>
                        <img loading="lazy" src={event.imageUrl} alt="Publication" style={{ maxWidth: '100%', borderRadius: 'var(--border-radius-sm)' }} />
                      </div>
                    )}
                    {event.sharedRoutine && (
                      <div style={{ padding: '0.8rem', marginTop: event.text || event.imageUrl ? '0.8rem' : 0, backgroundColor: 'rgba(162, 155, 254, 0.1)', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--primary-color)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'bold', color: 'var(--primary-color)', marginBottom: '0.5rem' }}>
                          <Dumbbell size={16} /> Programme partagé : {event.sharedRoutine.name}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                          {event.sharedRoutine.exercises.map(e => e.type === 'superset' ? e.items.map(i => i.name).join(' + ') : e.name).join(', ')}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              // Event type: workout
              // Calculate volume
              const totalVolume = event.totalVolume !== undefined && event.totalVolume !== null
                ? event.totalVolume
                : calculateWorkoutVolume(event);

              const formatDuration = (min) => {
                if (!min) return '';
                if (min < 60) return `${min}mins`;
                const h = Math.floor(min / 60);
                const m = min % 60;
                return `${h}h${m.toString().padStart(2, '0')}mins`;
              };

              const feelingEmojis = {
                'easy': '😊 Facile',
                'normal': '😐 Normal',
                'hard': '🥵 Difficile',
                'extreme': '💀 Extrême'
              };
              const feelingText = event.feeling ? feelingEmojis[event.feeling] : '';

              return (
                <div key={`workout-${event.id}`} className="card" style={{ padding: '1rem' }}>
                  <div className="flex items-center gap-3 mb-3">
                    <UserAvatar profile={p} size={40} onClick={() => navigate(`/profile/${p.id}`)} style={{ flexShrink: 0 }} />
                    <div>
                      <strong 
                        style={{ fontSize: '1.05rem', cursor: 'pointer' }} 
                        onClick={() => navigate(`/profile/${p.id}`)}
                        onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                        {p.name}
                      </strong>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{date}</div>
                    </div>
                  </div>
                  
                  {event.unlockedBadges && event.unlockedBadges.length > 0 && (
                    <div style={{ marginBottom: '0.8rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {event.unlockedBadges.map(badge => (
                        <span key={badge.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', backgroundColor: 'rgba(255, 215, 0, 0.15)', color: '#FFD700', padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 'bold' }}>
                          🏆 A débloqué : {badge.name}
                        </span>
                      ))}
                    </div>
                  )}

                  <div style={{ padding: '0.8rem', backgroundColor: 'var(--bg-color)', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--primary-color)', color: '#fff', width: '42px', height: '42px', borderRadius: '50%' }}>
                      <Dumbbell size={22} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '0.95rem', marginBottom: '0.2rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {event.routineName || event.name ? `A terminé un entraînement : ${event.routineName || event.name}` : (event.muscles && event.muscles.length > 0 ? `A terminé un entraînement : Séance ${event.muscles.join(', ')}` : 'A terminé un entraînement')}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.8rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        {totalVolume > 0 && <span style={{ color: 'var(--secondary-color)', fontWeight: 'bold' }}>{totalVolume} kg soulevés</span>}
                        {event.duration && <span style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}><Clock size={12}/> {formatDuration(event.duration)}</span>}
                        {feelingText && <span>{feelingText}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {activeTab === 'boss' && (
        <WorldBossView currentProfile={currentProfile} embedded={true} />
      )}

      {activeTab === 'friends' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {friendsList.length === 0 ? (
            <div className="card text-center text-secondary">
              Tu n'as pas encore d'amis. Utilise l'onglet Ajouter !
            </div>
          ) : (
            friendsList.map(friendId => {
              const p = profiles[friendId];
              if (!p) return null;
              return (
                <div key={friendId} className="card flex items-center justify-between" style={{ padding: '1rem' }}>
                  <div className="flex items-center gap-3">
                    <UserAvatar profile={p} size={50} onClick={() => navigate(`/profile/${p.id}`)} style={{ flexShrink: 0 }} />
                    <div>
                      <h3 
                        style={{ margin: 0, fontSize: '1.1rem', cursor: 'pointer' }} 
                        onClick={() => navigate(`/profile/${p.id}`)}
                        onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                        {p.name}
                      </h3>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Niveau {getLevelInfo(p.xp || 0).level}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn-icon" title="Envoyer un message" onClick={() => navigate(`/messages?user=${p.id}`)} style={{ color: 'var(--primary-color)' }}>
                      <Mail size={20} />
                    </button>
                    <button className="btn-icon" title="Retirer" onClick={() => setShowRemoveModal(friendId)} style={{ color: 'var(--danger-color)' }}>
                      <UserX size={20} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
          
          {showRemoveModal && profiles[showRemoveModal] && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
              <div className="card" style={{ width: '100%', maxWidth: '400px', padding: '1.5rem' }}>
                <h3 style={{ marginTop: 0, color: 'var(--danger-color)' }}>Retirer un ami</h3>
                <p>Veux-tu vraiment retirer <strong>{profiles[showRemoveModal].name}</strong> de tes amis ?</p>
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                  <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowRemoveModal(null)}>Annuler</button>
                  <button className="btn btn-primary" style={{ flex: 1, backgroundColor: 'var(--danger-color)', borderColor: 'var(--danger-color)' }} onClick={() => handleRemoveFriend(showRemoveModal)}>
                    Oui, retirer
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'add' && (
        <div className="card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ marginTop: 0, marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>Ton Code Ami</h3>
            <div style={{ 
              display: 'inline-flex', alignItems: 'center', gap: '1rem', 
              backgroundColor: 'var(--bg-color)', border: '2px dashed var(--primary-color)', 
              padding: '1rem 2rem', borderRadius: 'var(--border-radius)', fontSize: '1.5rem', fontWeight: 'bold', letterSpacing: '2px'
            }}>
              {friendCode || "..."}
              <button className="btn-icon" onClick={handleCopyCode} title="Copier le code">
                <Copy size={24} color="var(--primary-color)" />
              </button>
            </div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
              Partage ce code pour que tes amis puissent t'ajouter.
            </p>
          </div>

          <hr style={{ borderColor: 'var(--border-color)', margin: '0' }} />

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <h3 style={{ marginTop: 0, marginBottom: '1.5rem', textAlign: 'center' }}>Ajouter quelqu'un</h3>
            <form onSubmit={handleSendRequestByCode} className="add-friend-form" style={{ display: 'flex', gap: '0.8rem', width: '100%', maxWidth: '400px', boxSizing: 'border-box' }}>
              <input 
                type="text" 
                placeholder="Ex: 8X2F9A" 
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                style={{ 
                  flex: 1, 
                  textTransform: 'uppercase', 
                  letterSpacing: '2px', 
                  fontWeight: 'bold',
                  padding: '0.8rem',
                  fontSize: '1.2rem',
                  textAlign: 'center',
                  borderRadius: 'var(--border-radius)',
                  border: '2px solid var(--border-color)',
                  backgroundColor: 'var(--bg-color)',
                  color: 'var(--text-primary)'
                }}
              />
              <button type="submit" className="btn btn-primary" disabled={!codeInput.trim()} style={{ padding: '0 1.5rem', fontWeight: 'bold' }}>
                <UserPlus size={20} /> Ajouter
              </button>
            </form>
          </div>

        </div>
      )}

      {activeTab === 'requests' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          
          {/* Reçues */}
          <div>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.1rem', color: 'var(--text-secondary)' }}>Reçues</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {receivedRequests.length === 0 ? (
                <div className="card text-center text-secondary">
                  Aucune demande reçue.
                </div>
              ) : (
                receivedRequests.map(reqId => {
                  const p = profiles[reqId];
                  if (!p) return null;
                  return (
                    <div key={reqId} className="card flex items-center justify-between" style={{ padding: '1rem' }}>
                      <div className="flex items-center gap-3">
                        <UserAvatar profile={p} size={40} onClick={() => navigate(`/profile/${p.id}`)} style={{ flexShrink: 0 }} />
                        <div>
                          <strong 
                            style={{ fontSize: '1rem', cursor: 'pointer' }}
                            onClick={() => navigate(`/profile/${p.id}`)}
                            onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                            onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
                          >
                            {p.name}
                          </strong>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button className="btn btn-primary" style={{ padding: '0.5rem 1rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }} onClick={() => handleAcceptRequest(reqId)}>
                          <Check size={16} /> Accepter
                        </button>
                        <button className="btn btn-outline" style={{ padding: '0.5rem 1rem', fontSize: '0.85rem', color: 'var(--danger-color)', borderColor: 'var(--danger-color)', display: 'flex', alignItems: 'center', gap: '0.4rem' }} onClick={() => handleRejectRequest(reqId)}>
                          <X size={16} /> Refuser
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Envoyées */}
          <div>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.1rem', color: 'var(--text-secondary)' }}>Envoyées</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {sentRequests.length === 0 ? (
                <div className="card text-center text-secondary">
                  Aucune demande envoyée.
                </div>
              ) : (
                sentRequests.map(reqId => {
                  const p = profiles[reqId];
                  if (!p) return null;
                  return (
                    <div key={reqId} className="card flex items-center justify-between" style={{ padding: '1rem' }}>
                      <div className="flex items-center gap-3">
                        <UserAvatar profile={p} size={40} onClick={() => navigate(`/profile/${p.id}`)} style={{ flexShrink: 0 }} />
                        <div>
                          <strong 
                            style={{ fontSize: '1rem', cursor: 'pointer' }}
                            onClick={() => navigate(`/profile/${p.id}`)}
                            onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                            onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
                          >
                            {p.name}
                          </strong>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>En attente</div>
                        </div>
                      </div>
                      <button className="btn btn-outline" style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }} onClick={() => handleCancelRequest(reqId)}>
                        Annuler
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default Social;
